import type { MeasurementReviewField } from "./measurement-review";

function openingAxis(field: MeasurementReviewField) {
  if (field.unit !== "m" || !(
    field.subjectKind === "opening" || /\b(?:opening|doorway|knock[- ]?through)\b/i.test(field.label)
  )) return undefined;
  return field.label.match(/\b(height|width|length|span)\)?\s*$/i)?.[1].toLowerCase();
}

function sameOpening(a: MeasurementReviewField, b: MeasurementReviewField) {
  if (a.subjectId && b.subjectId) return a.subjectId === b.subjectId;
  const scope = (f: MeasurementReviewField) => (f.subjectLabel || f.label)
    .replace(/\b(?:height|width|length|span)\)?\s*$/i, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return scope(a) === scope(b);
}

/** A height must not displace its opening's horizontal span during UI selection. */
export function withOpeningWidthFields(selected: MeasurementReviewField[], available = selected) {
  const result = [...selected];
  for (const height of selected) {
    if (height.removed || openingAxis(height) !== "height") continue;
    const horizontal = available.filter(f => /^(?:width|length|span)$/.test(openingAxis(f) || "") && sameOpening(f, height));
    // Preserve deliberate dismissals rather than regenerating the same suggestion.
    if (horizontal.length && horizontal.every(f => f.removed)) continue;
    let width = result.find(f => !f.removed && /^(?:width|length|span)$/.test(openingAxis(f) || "") && sameOpening(f, height));
    const candidates = horizontal.filter(f => !f.removed);
    if (!width && candidates.length === 1) width = candidates[0];
    if (!width) {
      const id = `review-width-${height.id}`.slice(0, 100);
      if (available.some(f => f.id === id && f.removed)) continue;
      width = {
        id, label: height.label.replace(/\bheight(?=\)?\s*$)/i, "width"),
        unit: "m", value: "", note: "Clear horizontal opening width/length. Enter the actual span; leave blank if unknown.",
        subjectId: height.subjectId, subjectLabel: height.subjectLabel, subjectKind: height.subjectKind,
        custom: height.custom,
      };
    }
    const existing = result.findIndex(f => f.id === width.id);
    if (existing >= 0 && existing < result.indexOf(height)) continue;
    if (existing >= 0) result.splice(existing, 1);
    result.splice(result.indexOf(height), 0, width);
  }
  return result;
}

function locationKey(label: string) {
  const location = label.match(/\b(?:opening|doorway)\b(.+?)(?:\(?\b(?:width|length)\)?\s*$)/i)?.[1];
  if (!location) return "";
  return location.toLowerCase()
    .replace(/\b\d+(?:\.\d+)?\s*[×x]\s*\d+(?:\.\d+)?(?:\s*[×x]\s*\d+(?:\.\d+)?)?\s*(?:ub|uc)?\b/g, "")
    .replace(/\b(?:the|a|an|new|existing|at|between|on|in|into|to|and|with|beam|steel|ub|uc)\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ").trim();
}

/** One rough opening width per named location in the optional review.
 * Original member evidence is untouched; an estimate is not a measured clear span.
 */
export function consolidateOpeningReviewFields(fields: MeasurementReviewField[]) {
  const result = [...fields];
  const estimates = fields.filter(f => !f.custom && (f.openingEstimate ||
    f.descriptionBasis === "assumed" && /\bbeam over opening\b.*\blength\s*$/i.test(f.label)));
  const handled = new Set<string>();
  for (const estimate of estimates) {
    const key = locationKey(estimate.label);
    if (!key || handled.has(key)) continue;
    handled.add(key);
    const peers = result.filter(f => !f.custom && locationKey(f.label) === key &&
      (estimates.includes(f) || f.openingEstimate || /\bwidth\)?\s*$/i.test(f.label)));
    const changed = peers.find(f => !f.removed && (f.edited || f.original?.source === "entered" ||
      f.original?.source === "measured" && !f.drawingLimit) &&
      f.value !== estimate.value);
    const chosen = changed || estimate;
    const label = estimate.label.replace(/^Beam over opening\b/i, "Opening")
      .replace(/\blength\s*$/i, "width");
    const field: MeasurementReviewField = {
      ...chosen, label, reviewLabel: undefined, subjectId: undefined, subjectLabel: undefined, subjectKind: undefined,
      // Do not carry a beam evidence id or design minimum onto an estimated opening width.
      original: changed?.original, drawingLimit: changed?.drawingLimit,
      descriptionBasis: "assumed", openingEstimate: true,
      removed: estimate.removed || chosen.removed,
      note: chosen.openingEstimate ? chosen.note :
        `Approximate opening width for estimating, based on the description's assumed beam length; not a measured clear opening. ${chosen.note}`.slice(0, 1000),
    };
    const ids = new Set([...peers.map(f => f.id), estimate.id]);
    const first = result.findIndex(f => ids.has(f.id));
    const remaining = result.filter(f => !ids.has(f.id));
    remaining.splice(first, 0, field);
    result.splice(0, result.length, ...remaining);
  }
  return result;
}
